/* eslint-disable max-len */
import {
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  VALIDATION_INVALIDLEAID,
  VALIDATION_INVALIDPOTENTIALLEAID,
} from 'src/components/FileUpload/UploadConstants';
import ActionButton from '../../components/ActionButton/ActionButton';
import { FileUploadLegend } from '../../components/FileUpload/FileUploadLegend/FileUploadLegend';
import NotAbleToUpload from '../../components/FileUpload/NotAbleToUpload/NotAbleToUpload';
import ReadyToUpload from '../../components/FileUpload/ReadyToUpload/ReadyToUpload';
import SelectFiles from '../../components/FileUpload/SelectFiles/SelectFiles';
import FileUploadStatus from '../../components/FileUploadStatus/FileUploadStatus';
import Modal from '../../components/Modal/Modal';
import NavigationPage from '../../components/NavigationPage/NavigationPage';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setSelectedDistrict } from '../../redux/slices/appSlice';
import { errorLoaded } from '../../redux/slices/errorSlice';
import {
  selectFileLoading,
  selectFilePermissions,
} from '../../redux/slices/fileSlice';
import {
  fetchPermissionsMap,
  fetchUploadFormModel,
} from '../../redux/thunks/filesThunks';
import {
  abortPromiseOnUnmount,
  getCurrentUserAccess,
  isValidCode,
  setCurrentUserAccess,
} from '../../services/base.service';
import { upload } from '../../services/file.service';
import { appRoutePaths } from '../../services/route.service';
import { updateCurrentDistrict } from '../../services/user.service';
import { ApiResponseModel } from '../../types/ApiResponseModel';
import { AxiosProgressEvent } from '../../types/AxiosProgressEvent';
import { DialogType } from '../../types/DialogTypes';
import { FileDetail } from '../../types/FileDetail';
import { FileError } from '../../types/FileError';
import { FilePermissionMap } from '../../types/FilePermissionMap';
import { FileUploadFormModel } from '../../types/FileUploadFormModel';
import { validateFiles } from '../../utilities/uploadUtilities';
import './FileUpload.css';

const FileUpload = (): ReactElement => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectFileLoading);
  const isLoadingPermissions = useAppSelector(selectFileLoading);
  const filePermissions: FilePermissionMap[] = useAppSelector(
    selectFilePermissions
  );

  const [unfilteredFiles, setUnfilteredFiles] = useState<File[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<File[]>([]);
  const [filteredOutFiles, setFilteredOutFiles] = useState<File[]>([]);
  const [filteredOutFilesErrors, setFilteredOutFilesErrors] = useState<
    string[]
  >([]);
  const [solutionList, setSolutionList] = useState<string[]>([]);

  const [files, setFiles] = useState<FileDetail[]>([]);
  const [fileErrors, setFileErrors] = useState<FileError[]>([]);
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [uploadState, setUploadState] = useState('idle');
  const [userAllowedFileEndings, setUserAllowedFileEndings] = useState<
    string[]
  >([]);
  const fileUploadFormModelDefault: FileUploadFormModel = {
    allowedFileEndings: [],
    allowedFileExtensions: [],
  };

  const [userAllowedFormModel, setUserAllowedFormModel] =
    useState<FileUploadFormModel>(fileUploadFormModelDefault);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingNavigationRoute, setPendingNavigationRoute] = useState<
    string | null
  >(null);
  const [pendingDistrictInfo, setPendingDistrictInfo] = useState<{
    name: string;
    value: string;
  } | null>(null);

  const navigate = useNavigate();

  const filterFiles = (): void => {
    const { validFiles, invalidFiles, errorMessages, solutions } =
      validateFiles(unfilteredFiles);
    setFilteredFiles(validFiles);
    setFilteredOutFiles(invalidFiles);

    setFilteredOutFilesErrors(errorMessages);
    setSolutionList(solutions);
  };

  const getUniqueValues = (arr: string[]): string[] => {
    return Array.from(new Set(arr));
  };

  const aggregateExtensions = useCallback(
    (_filePermissions: FilePermissionMap[]): string[] => {
      return getUniqueValues(
        _filePermissions.length > 0
          ? _filePermissions.flatMap((permission) => permission.fileExtensions)
          : []
      );
    },
    []
  );

  const getFileTypeFromName = (fileName: string): string => {
    // Find the matching file permission based on filename containing permission strings
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    for (const filePermission of filePermissions) {
      if (fileName.includes(filePermission.fileType)) {
        for (const ext of filePermission.fileExtensions) {
          if (ext.toLowerCase() === fileExtension) {
            return filePermission.fileType + '.' + ext.toLowerCase(); // Return the matching permission string with the correct extension
          }
        }
      }
    }

    // Fallback to the original filename if no match found
    return fileName;
  };

  const progressHandler = (e: AxiosProgressEvent): void => {
    setLoaded(e.loaded);
    setTotal(e.total ?? 0);
  };

  const completeHandler = (response: ApiResponseModel<unknown>): void => {
    if (response.status >= 400) {
      setUploadState('failed');
      if (response.error.dataObj) {
        setFileErrors(response.error.dataObj as FileError[]);
      } else {
        setFileErrors(
          filteredFiles.map(
            (): FileError => ({
              errors: ['There was an error uploading the file.'],
            })
          )
        );
      }
    } else {
      setUploadState('done');
    }
  };

  const errorHandler = (): void => {
    setUploadState('failed');
  };

  const uploadAgainHandler = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();

    setUnfilteredFiles([]);
    setFiles([]);
  };

  const submitHandler = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();

    setFiles(
      filteredFiles.map(
        (file, i): FileDetail => ({
          name: file.name,
          progress: 0,
          size: file.size,
          key: i.toString(),
          fileType: getFileTypeFromName(file.name),
        })
      )
    );

    setUploadState('uploading');

    upload({
      files: filteredFiles,
      onProgress: progressHandler,
      onError: errorHandler,
      onDone: completeHandler,
    });
  };

  const closeConfirmModal = (): void => {
    setShowConfirmModal(false);
    setPendingNavigationRoute(null);
    setPendingDistrictInfo(null);
  };

  const hasFilesSelected = useCallback((): boolean => {
    return (
      (unfilteredFiles.length > 0 || filteredFiles.length > 0) &&
      uploadState === 'idle'
    );
  }, [unfilteredFiles, filteredFiles, uploadState]);

  const handleNavigateWithConfirmation = (route: string): void => {
    if (hasFilesSelected()) {
      setPendingNavigationRoute(route);
      setShowConfirmModal(true);
    } else {
      navigate(route);
    }
  };

  const handleConfirmYes = async (): Promise<void> => {
    setShowConfirmModal(false);

    if (pendingNavigationRoute === 'district-change' && pendingDistrictInfo) {
      setUnfilteredFiles([]);
      setFilteredFiles([]);

      try {
        const response = await updateCurrentDistrict(pendingDistrictInfo.value);

        if (isValidCode(response.status)) {
          setCurrentUserAccess(response.data);

          const user = getCurrentUserAccess();
          if (user?.currentDistrict) {
            dispatch(setSelectedDistrict(user.currentDistrict));
          }
          navigate(0);
        } else {
          dispatch(errorLoaded(response.error));
        }
      } catch (error) {}

      setPendingNavigationRoute(null);
      setPendingDistrictInfo(null);
    } else if (pendingNavigationRoute) {
      navigate(pendingNavigationRoute);
      setPendingNavigationRoute(null);
    }
  };

  const handleConfirmNo = (): void => {
    setShowConfirmModal(false);
    setPendingNavigationRoute(null);
    setPendingDistrictInfo(null);
  };

  useEffect(() => {
    if (uploadState === 'uploading') {
      setIsFailed(false);
      setIsCompleted(false);
    } else if (uploadState === 'done') {
      setIsFailed(false);
      setIsCompleted(true);
      setLoaded(1);
      setTotal(1);
    } else if (uploadState === 'failed') {
      setIsFailed(true);
    }
  }, [uploadState]);

  useEffect(() => {
    filterFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfilteredFiles]);

  useEffect(() => {
    const filterUserFiles = (): void => {
      if (filePermissions.length > 0) {
        setUserAllowedFileEndings(
          filePermissions.map((filePermission) => filePermission.fileType)
        );
      } else {
        setUserAllowedFileEndings([]);
      }
    };
    filterUserFiles();
  }, [filePermissions]);

  useEffect(() => {
    setUserAllowedFormModel({
      allowedFileEndings: userAllowedFileEndings,
      allowedFileExtensions: aggregateExtensions(filePermissions),
    });
  }, [userAllowedFileEndings, filePermissions, aggregateExtensions]);

  useEffect(() => {
    const promise = dispatch(fetchUploadFormModel());
    dispatch(fetchPermissionsMap());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | void => {
      if (hasFilesSelected()) {
        e.preventDefault();
        e.returnValue =
          'You have not uploaded your file. Do you wish to cancel the upload?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [unfilteredFiles, filteredFiles, uploadState, hasFilesSelected]);

  useEffect(() => {
    const handleDistrictClick = (e: Event): void => {
      const target = e.target as HTMLElement;

      if (!target || typeof target.className !== 'string') {
        return;
      }

      const hasDropdownClass = target.className.includes('dropdown-option');
      const hasReactSelectId = target.id?.includes('react-select') || false;
      const hasParentheses = target.textContent?.includes('(') || false;
      const isDistrictOption =
        hasDropdownClass && hasReactSelectId && hasParentheses;

      if (isDistrictOption && hasFilesSelected()) {
        e.preventDefault();
        e.stopPropagation();

        const districtName = target.textContent || '';
        const districtId = districtName.match(/\(([^)]+)\)$/)?.[1] || '';

        setPendingDistrictInfo({ name: districtName, value: districtId });
        setPendingNavigationRoute('district-change');
        setShowConfirmModal(true);
      }
    };

    document.addEventListener('click', handleDistrictClick, true);

    return () => {
      document.removeEventListener('click', handleDistrictClick, true);
    };
  }, [unfilteredFiles, filteredFiles, uploadState, hasFilesSelected]);

  useEffect(() => {
    const handleLinkClick = (e: Event): void => {
      const target = e.target as HTMLElement;

      const link = target.closest('a[href]') as HTMLAnchorElement;

      const button = target.closest(
        'button[data-navigation], .navigation-button'
      ) as HTMLButtonElement;

      const logoutButton = target.closest(
        'button[data-testid="logout-link"], button[data-testid="idle-logout-btn"]'
      ) as HTMLButtonElement;

      if (link && hasFilesSelected()) {
        const href = link.getAttribute('href');
        if (href && href.startsWith('/') && !href.startsWith('//')) {
          e.preventDefault();
          e.stopPropagation();
          setPendingNavigationRoute(href);
          setShowConfirmModal(true);
        }
      } else if (button && hasFilesSelected()) {
        const navRoute = button.getAttribute('data-navigation');
        if (navRoute) {
          e.preventDefault();
          e.stopPropagation();
          setPendingNavigationRoute(navRoute);
          setShowConfirmModal(true);
        }
      } else if (logoutButton && hasFilesSelected()) {
        e.preventDefault();
        e.stopPropagation();
        setPendingNavigationRoute('/logout');
        setShowConfirmModal(true);
      }
    };

    document.addEventListener('click', handleLinkClick, true);

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [unfilteredFiles, filteredFiles, uploadState, hasFilesSelected]);

  let showSelectFiles = false;
  let showReadyToUpload = false;
  let showNotAbleToUpload = false;
  let showClearFiles = false;
  let showUploadFiles = false;
  let showRetryOptions = false;
  let showInvalidLeaIdError = false;

  const hasValidFilesToUpload = filteredFiles && filteredFiles.length > 0;
  const hasNoValidFilesReadyToUpload = !hasValidFilesToUpload;
  const hasErrorFilesToUpload = filteredOutFiles && filteredOutFiles.length > 0;
  const hasNoErrorFilesReadyToUpload = !hasErrorFilesToUpload;
  const hasNoValidFileButHasErrorFiles =
    hasNoValidFilesReadyToUpload && hasErrorFilesToUpload;

  if (files.length === 0) {
    showSelectFiles =
      unfilteredFiles.length === 0 ||
      (hasNoValidFilesReadyToUpload && hasNoErrorFilesReadyToUpload);
    showReadyToUpload = hasValidFilesToUpload;
    showNotAbleToUpload = hasErrorFilesToUpload;
    showClearFiles = hasNoValidFileButHasErrorFiles || hasValidFilesToUpload;
    showUploadFiles = hasValidFilesToUpload;
  }
  if (filteredOutFilesErrors.includes(VALIDATION_INVALIDPOTENTIALLEAID())) {
    showInvalidLeaIdError = true;
  }
  if (filteredOutFilesErrors.includes(VALIDATION_INVALIDLEAID())) {
    showInvalidLeaIdError = true;
  }
  showRetryOptions = files.length > 0;

  return (
    <NavigationPage
      heading={'Upload a New File'}
      pageClass="files--upload"
      isLoading={isLoading || isLoadingPermissions}
      loadingDataId="file-upload-loader"
      loadingText="Getting upload details"
      data-testid="file-upload-page"
    >
      {userAllowedFormModel && userAllowedFormModel.allowedFileExtensions && (
        <FileUploadLegend formModel={userAllowedFormModel} />
      )}
      {showSelectFiles && <SelectFiles onChange={setUnfilteredFiles} />}
      {showReadyToUpload && filteredFiles.length > 0 && (
        <ReadyToUpload files={filteredFiles} onChange={setFilteredFiles} />
      )}
      {showInvalidLeaIdError && (
        <NotAbleToUpload
          files={filteredOutFiles}
          errors={filteredOutFilesErrors}
          solutions={solutionList}
          customTitle="File will not be uploaded."
        />
      )}
      {!showInvalidLeaIdError && showNotAbleToUpload && (
        <NotAbleToUpload
          files={filteredOutFiles}
          errors={filteredOutFilesErrors}
          solutions={solutionList}
        />
      )}
      {(showClearFiles || showUploadFiles) && (
        <div className="files--upload--buttons">
          {showClearFiles && (
            <button
              data-cy="clear-files-button"
              className="button button--large no-wrap-text"
              onClick={() => {
                setUnfilteredFiles([]);
                setFilteredFiles([]);
                setFilteredOutFiles([]);
                setFiles([]);
                setUploadState('idle');
              }}
            >
              Clear Files
            </button>
          )}
          {showUploadFiles && (
            <button
              data-cy="upload-files-button"
              className="button button--large button--secondary no-wrap-text"
              onClick={
                submitHandler as unknown as MouseEventHandler<HTMLButtonElement>
              }
            >
              Upload {filteredFiles.length}{' '}
              {filteredFiles.length > 1 ? 'Files' : 'File'}
            </button>
          )}
        </div>
      )}
      <div className="files--upload--statuses">
        {files.map((file, i) => {
          let percent = 0;
          if (isCompleted) {
            percent = 1;
          } else if (total) {
            percent = loaded / total;
          }
          let error = '';
          if (
            fileErrors[i] &&
            fileErrors[i].errors &&
            fileErrors[i].errors.length
          ) {
            // eslint-disable-next-line prefer-destructuring
            error = fileErrors[i].errors[0];
          }
          return (
            <FileUploadStatus
              name={file.name}
              fileType={file.fileType}
              percent={percent}
              isFailed={isFailed}
              isCompleted={isCompleted}
              error={error}
              key={file.key}
            />
          );
        })}
      </div>
      {showRetryOptions && (isCompleted || isFailed) && (
        <div className="files--upload--retry">
          {isCompleted && (
            <button
              className="button button--large button--secondary no-wrap-text"
              onClick={() =>
                handleNavigateWithConfirmation(appRoutePaths.SubmissionStatus)
              }
            >
              View Files
            </button>
          )}
          <button
            className="button button--large no-wrap-text"
            onClick={
              uploadAgainHandler as unknown as MouseEventHandler<HTMLButtonElement>
            }
          >
            {isFailed ? 'Try Again' : 'Upload More Files'}
          </button>
        </div>
      )}
      {showConfirmModal && (
        <Modal
          type={DialogType.CONFIRM}
          open={showConfirmModal}
          onClose={closeConfirmModal}
          title={'Are you sure?'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              You have not uploaded your file. Do you wish to cancel the upload?
            </p>
            <div className="button-row">
              <ActionButton
                onClick={handleConfirmYes}
                dataTestId="yes-button"
                tooltipText={'Continue navigation'}
              >
                <span>Yes</span>
              </ActionButton>
              <ActionButton
                classes="button--secondary"
                onClick={handleConfirmNo}
                dataTestId="no-button"
                tooltipText={'Stay on current page'}
              >
                <span>No</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </NavigationPage>
  );
};

export default FileUpload;
